import { createSlice } from '@reduxjs/toolkit';
import { loginUser, logoutUser, forgotPassword, verifyOTP, resetPassword } from '../actions/authActions';
import { AuthState, User } from '../types/authTypes';

// Load auth state from local storage
const loadAuthState = (): AuthState => {
  try {
    const serializedState = localStorage.getItem('auth');
    return serializedState
      ? JSON.parse(serializedState)
      : {
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
          error: null,
          forgotPasswordSuccess: false,
          otpVerified: false,
          verificationToken: null,
          resetPasswordSuccess: false,
          userExists: true,
        };
  } catch (error) {
    console.error('Failed to load auth state from local storage:', error);
    return {
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,
      forgotPasswordSuccess: false,
      otpVerified: false,
      verificationToken: null,
      resetPasswordSuccess: false,
      userExists: true,
    };
  }
};

// Save auth state to local storage
const saveAuthState = (state: AuthState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('auth', serializedState);
  } catch (error) {
    console.error('Failed to save auth state to local storage:', error);
  }
};

// Process user permissions - specifically check for role permissions
const processUserPermissions = (user: User | null): User | null => {
  if (!user) return null;

  // Convert permissions array to object if it's an array
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    // Use the first permissions object from the array
    const permissionsObj = user.permissions[0];

    // Check if user is superAdmin - they have all permissions
    if (user.designation === 'superAdmin') {
      return {
        ...user,
        permissions: {
          ...permissionsObj,
          roleRead: true,
          roleWrite: true,
          roleEdit: true,
          // Ensure all other permissions are true for superAdmin
          customerRead: true,
          customerWrite: true,
          customerEdit: true,
          employeeRead: true,
          employeeWrite: true,
          employeeEdit: true,
          projectRead: true,
          projectWrite: true,
          projectEdit: true,
          canAssignProject: true,
          canInProgress: true,
          canReview: true,
          canDeliverProject: true,
          canBillProject: true,
          canHoldProject: true,
          canCancelProject: true,
          canReceivePayment: true,
          canReopenProject: true,
        },
      };
    }

    // For other users, use their permissions as is
    return {
      ...user,
      permissions: permissionsObj,
    };
  }

  // If permissions is already an object (not an array), just do the original processing
  if (
    user.permissions &&
    typeof user.permissions === 'object' &&
    !Array.isArray(user.permissions)
  ) {
    // Check if user is superAdmin - they have all permissions
    if (user.designation === 'superAdmin') {
      return {
        ...user,
        permissions: {
          ...(user.permissions || {}),
          roleRead: true,
          roleWrite: true,
          roleEdit: true,
          // Ensure all other permissions are true for superAdmin
          customerRead: true,
          customerWrite: true,
          customerEdit: true,
          employeeRead: true,
          employeeWrite: true,
          employeeEdit: true,
          projectRead: true,
          projectWrite: true,
          projectEdit: true,
          canAssignProject: true,
          canInProgress: true,
          canReview: true,
          canDeliverProject: true,
          canBillProject: true,
          canHoldProject: true,
          canCancelProject: true,
          canReceivePayment: true,
          canReopenProject: true,
        },
      };
    }

    // For other users, check if they have the specific role permissions
    if (!user.permissions.roleRead) {
      const { roleRead, roleWrite, roleEdit, ...otherPermissions } = user.permissions;
      return {
        ...user,
        permissions: otherPermissions,
      };
    }
  }

  return user;
};

const initialState: AuthState = loadAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthState: state => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.error = null;
      state.forgotPasswordSuccess = false;
      state.otpVerified = false;
      state.verificationToken = null;
      state.resetPasswordSuccess = false;
      state.userExists = true;
      saveAuthState(state); // Save cleared state to local storage

      // Also clear users data from localStorage directly
      localStorage.removeItem('users-data');
    },
    clearAuthError: state => {
      state.error = null;
    },
    resetForgotPasswordState: state => {
      state.forgotPasswordSuccess = false;
      state.otpVerified = false;
      state.verificationToken = null;
      state.error = null;
      state.userExists = true;
      state.loading = false; // Also reset loading state
    },
    resetAll: state => {
      // Complete state reset - more thorough than resetForgotPasswordState
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.forgotPasswordSuccess = false;
      state.otpVerified = false;
      state.verificationToken = null;
      state.resetPasswordSuccess = false;
      state.userExists = true;
      // Don't clear user/token to preserve login state if needed
    },
    updateUserPermissions: (state, action) => {
      if (state.user && action.payload) {
        // Handle both object and array formats
        if (Array.isArray(state.user.permissions)) {
          // If current permissions is an array, update the first object
          if (state.user.permissions.length > 0) {
            state.user.permissions[0] = {
              ...state.user.permissions[0],
              ...action.payload,
            };
          } else {
            // If empty array, add a new permissions object
            state.user.permissions = [action.payload];
          }
        } else {
          // If current permissions is an object, update it directly
          state.user.permissions = {
            ...state.user.permissions,
            ...action.payload,
          };
        }
        state.user = processUserPermissions(state.user);
        saveAuthState(state);
      }
    },
  },
  extraReducers: builder => {
    builder
      // Handle Login
      .addCase(loginUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        // @ts-ignore
        let user = action.payload?.data?.admin || action.payload?.data?.employee || null;

        // Process user permissions before storing
        state.user = processUserPermissions(user);
        state.token = action.payload.token;
        saveAuthState(state); // Save updated state to local storage
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
        saveAuthState(state); // Save state with error to local storage
      })

      // Handle Logout
      .addCase(logoutUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.loading = false;
        state.error = null;
        saveAuthState(state); // Save cleared state to local storage

        // Also clear users data from localStorage directly
        localStorage.removeItem('users-data');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Logout failed';
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        saveAuthState(state); // Save cleared state to local storage

        // Also clear users data from localStorage directly
        localStorage.removeItem('users-data');
      })

      // Forgot Password
      .addCase(forgotPassword.pending, state => {
        state.loading = true;
        state.error = null;
        state.forgotPasswordSuccess = false;
        // Don't reset userExists yet to avoid UI flicker
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordSuccess = action.payload.success !== false;
        
        // Handle user existence explicitly
        if (action.payload.hasOwnProperty('userExists')) {
          state.userExists = !!action.payload.userExists;
        } else {
          // Default to true if not specified
          state.userExists = true;
        }
        
        // Clear any previous error
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to process request';
        state.forgotPasswordSuccess = false;
        // Don't set userExists to false if the error is about the user not existing
        // This allows us to show the error message instead
      })

      // Verify OTP
      .addCase(verifyOTP.pending, state => {
        state.loading = true;
        state.error = null;
        state.otpVerified = false;
        // Don't reset verification token yet to avoid UI flicker
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        state.verificationToken = action.payload.token;
        // Clear any error
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to verify OTP';
        state.otpVerified = false;
        // Don't reset token on verification failure as it might be a typo
      })

      // Reset Password
      .addCase(resetPassword.pending, state => {
        state.loading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, state => {
        state.loading = false;
        state.resetPasswordSuccess = true;
        state.otpVerified = false;
        state.verificationToken = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to reset password';
        state.resetPasswordSuccess = false;
      });
  },
});

export const { clearAuthState, clearAuthError, resetForgotPasswordState, updateUserPermissions, resetAll } = authSlice.actions;
export default authSlice.reducer;
